import * as React from "react";
import { CalendarDays, ChevronRight } from "lucide-react";
import type { AppointmentWithContext } from "@taskiq/shared";
import { PersonAvatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/Feedback";
import { Identity } from "@/components/ui/Identity";
import { SkeletonRows, Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { cn } from "@/lib/cn";
import {
  dayKeyIn,
  formatDuration,
  formatTimeIn,
  toLocalDate,
  todayIn,
  toValue,
  parseValue,
} from "@/lib/dates";
import { isOverdue, pointerFrom, type OpenAppointment } from "@/lib/schedule";
import { StatusBadge } from "./StatusBadge";

/**
 * The schedule as a table, grouped by day.
 *
 * ── One component, two homes ───────────────────────────────────────────────
 *
 * The schedule's agenda view and the dashboard's agenda card are the same list
 * of the same rows, and they were two separate renderings of it - a table here
 * and a hand-rolled `<ul>` on the overview, which drifted apart on the day
 * heading, the time format and the empty state. A row that means one thing has
 * to look like one thing, so both now come through here.
 *
 * What genuinely differs is passed in. `surface="card"` drops the scroller and
 * the sticky heads, which belong to a fixed-height page rather than to a box
 * five rows tall. `showAssignee={false}` drops the one column the dashboard
 * cannot use: it is already the reader's own site and mostly their own work, so
 * the column would be the same name repeated down the page.
 *
 * `surface="flow"` is the schedule's agenda: an ordinary document that scrolls
 * with the window. A fortnight of rows inside a 400px-tall scroller nested in a
 * page that does not move is a worse version of the same list, and the reader
 * already has a scrollbar.
 *
 * ── Not a fallback, and not only for phones ────────────────────────────────
 *
 * A grid conveys duration and overlap through position, which is exactly the
 * information a screen reader cannot get out of it - and it is unusable at 380px
 * wide, where seven columns are 54 pixels each. This view carries the same rows
 * as real text in reading order, so it is the accessible route to the same data
 * and the one that works on the device half this workforce is holding.
 *
 * It is offered on every viewport for that reason rather than swapped in below a
 * breakpoint. A view somebody prefers should not be something they have to
 * shrink the window to reach.
 *
 * ── The day break is a heading, and it stays put ───────────────────────────
 *
 * On the `page` surface both the column head and the current day's break are
 * sticky, because the two facts you lose first when a long list scrolls are
 * which column you are in and which day you are in.
 *
 * `flow` has neither, and cannot: `.table-wrap` is `overflow-x: auto`, which
 * makes it the sticky containing block, and it has no height of its own to
 * scroll against - so a sticky head there simply never moves. Turning it into a
 * clipping box instead would trade away the horizontal scroll a five-column
 * table genuinely needs on a phone, for a nicety. The day breaks carry the
 * wayfinding on that surface, and they are drawn heavier because of it. That
 * needs
 * `border-collapse: separate` on this one table - a collapsed border belongs to
 * the table rather than to the cell, so it scrolls away from the sticky cell it
 * was drawn under and leaves the header floating over the rows with no edge.
 * `.agenda-table` in `app.css` draws those edges with a shadow instead.
 */
export function AgendaList({
  appointments,
  timezone,
  loading,
  onOpen,
  showAssignee = true,
  surface = "page",
  emptyTitle = "Nothing scheduled",
  emptyDescription = "No appointments fall in this range at this site. Try a wider range, or clear the filters.",
}: {
  appointments: AppointmentWithContext[];
  timezone: string;
  loading: boolean;
  onOpen: OpenAppointment;
  /** Off where the list is already one person's own work at one site. */
  showAssignee?: boolean;
  /**
   * `page` fills a fixed-height screen and scrolls itself. `flow` is the same
   * table as an ordinary document that scrolls with the window. `card` sits
   * inside a `Card`, which owns the border and the corners.
   */
  surface?: "page" | "flow" | "card";
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const groups = React.useMemo(() => {
    const map = new Map<string, AppointmentWithContext[]>();
    for (const appointment of appointments) {
      const key = dayKeyIn(appointment.scheduled_start, timezone);
      const list = map.get(key);
      if (list) list.push(appointment);
      else map.set(key, [appointment]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [appointments, timezone]);

  const today = toValue(todayIn(timezone));
  const columns = showAssignee ? 5 : 4;

  return (
    <Table
      className={cn("agenda-table", `agenda-table--${surface}`)}
      minWidth={showAssignee ? "md" : "sm"}
      head={
        <>
          <Th>Appointment</Th>
          <Th hideBelow="sm">Time</Th>
          {showAssignee && <Th hideBelow="md">Assigned to</Th>}
          <Th>Status</Th>
          <Th className="agenda__chevron-col">
            <span className="sr-only">Open</span>
          </Th>
        </>
      }
    >
      {loading && (
        <SkeletonRows
          columns={[
            { kind: "identity" },
            { kind: "text", hideBelow: "sm" },
            ...(showAssignee ? [{ kind: "text", hideBelow: "md" } as const] : []),
            { kind: "badge", width: "narrow" },
            { kind: "blank" },
          ]}
        />
      )}

      {!loading && appointments.length === 0 && (
        <tr>
          <TdSpan colSpan={columns}>
            <EmptyState icon={<CalendarDays />} title={emptyTitle} description={emptyDescription} />
          </TdSpan>
        </tr>
      )}

      {!loading &&
        groups.map(([key, items]) => (
          <React.Fragment key={key}>
            <tr className="agenda__daybreak">
              <TdSpan colSpan={columns}>
                <span className="agenda__day">
                  {/* Parsed rather than `new Date(key)`: that parses as UTC midnight
                    and formats as the previous day in any negative offset. */}
                  <span className="agenda__day-name">{dayLabel(key)}</span>
                  {key === today && <span className="agenda__day-pill">Today</span>}
                  <span className="agenda__day-count tnum">
                    {items.length} {items.length === 1 ? "appointment" : "appointments"}
                  </span>
                </span>
              </TdSpan>
            </tr>

            {items.map((appointment) => {
              const assignee = appointment.assignee;
              const name = assignee?.full_name ?? assignee?.email ?? null;

              return (
                <Tr
                  key={appointment.id}
                  onClick={(event) => onOpen(appointment, event.currentTarget, pointerFrom(event))}
                  className="agenda__row"
                >
                  <Td>
                    <Identity
                      media={
                        // Priority as the row's left marker, off the same ramp
                        // the calendar chip uses, so a row looks like the same
                        // appointment in both views - overdue included, which
                        // outranks the priority in both places.
                        <span
                          className={cn(
                            "agenda__marker",
                            isOverdue(appointment)
                              ? "agenda__marker--overdue"
                              : `agenda__marker--prio-${appointment.priority}`
                          )}
                          aria-hidden
                        />
                      }
                      title={appointment.title}
                      subtitle={
                        [
                          appointment.priority !== "normal"
                            ? `${appointment.priority} priority`
                            : null,
                          appointment.reference,
                          appointment.customer_name,
                        ]
                          .filter(Boolean)
                          .join(" · ") || undefined
                      }
                    />
                  </Td>

                  <Td hideBelow="sm">
                    <span className="agenda__time">
                      <span className="tnum">
                        {formatTimeIn(appointment.scheduled_start, timezone)} -{" "}
                        {formatTimeIn(appointment.scheduled_end, timezone)}
                      </span>
                      <span className="agenda__duration tnum">
                        {formatDuration(appointment.scheduled_start, appointment.scheduled_end)}
                      </span>
                    </span>
                  </Td>

                  {showAssignee && (
                    <Td hideBelow="md">
                      {name ? (
                        <span className="agenda__who">
                          <PersonAvatar person={assignee} size="sm" />
                          <span className="agenda__who-name">{name}</span>
                        </span>
                      ) : (
                        <Badge tone="caution">Unassigned</Badge>
                      )}
                    </Td>
                  )}

                  <Td>
                    <StatusBadge status={appointment.status} overdue={isOverdue(appointment)} />
                  </Td>

                  <Td align="right" className="agenda__chevron-col">
                    <ChevronRight className="agenda__chevron" aria-hidden />
                  </Td>
                </Tr>
              );
            })}
          </React.Fragment>
        ))}
    </Table>
  );
}

function dayLabel(key: string): string {
  const date = parseValue(key);
  if (!date) return key;

  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(toLocalDate(date));
}
